/**
 * Supabase Query Helpers
 *
 * Helper functions to prevent hitting PostgREST's default 1000-row limit.
 *
 * ## Strategy: `.limit(N)` for direct table queries
 * Use `.limit(N)` when querying real tables (e.g., `ministers`, `credential_requests`).
 * PostgREST translates `.limit(N)` to `LIMIT N` in SQL, overriding the default cap.
 *
 * ```typescript
 * // ✅ Direct table query
 * const { data } = await withMinistersLimit(supabase.from('ministers').select('*'));
 * ```
 *
 * ## ⚠️ Multi-JOIN views — use a single scoped query, NOT batching
 * OFFSET-based batching on a complex view (e.g. `vw_credential_request_ministers_extended`)
 * forces Postgres to re-execute the full JOIN tree from scratch for every batch,
 * which causes OOM on constrained instances. Instead, scope the query with an
 * indexed filter (e.g. `request_year`) and a hard row cap:
 *
 * ```typescript
 * // ✅ Single scoped query for views — see reports.service.ts
 * query = query.eq('request_year', 2026).limit(5000);
 * ```
 *
 * The helpers in this file apply strategy 1 (direct table queries).
 */

// Default limits for different entity types
export const QUERY_LIMITS = {
  /** Ministers: Large dataset, up to 10k records */
  MINISTERS: 10000,
  
  /** Credential Requests: Large dataset with ministers, up to 10k records */
  CREDENTIAL_REQUESTS: 10000,
  
  /** Churches: Future large dataset, up to 10k records */
  CHURCHES: 10000,
  
  /** Users: Small dataset, 1k should be enough */
  USERS: 1000,
  
  /** Territories (Districts/Regions): Small dataset, default is fine */
  TERRITORIES: 1000,
  
  /** Degrees: Very small dataset, default is fine */
  DEGREES: 1000,
  
  /** Reports/Analytics: Large dataset, up to 10k records */
  REPORTS: 10000,
} as const;

/**
 * Apply appropriate limit to a query based on entity type
 * 
 * Uses both .limit() and .range() to ensure we bypass PostgREST's default 1000-row limit.
 * 
 * @example
 * ```typescript
 * const query = supabase.from('ministers').select('*');
 * const limitedQuery = applyQueryLimit(query, 'MINISTERS');
 * ```
 */
export function applyQueryLimit<T>(
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  query: any,
  entityType: keyof typeof QUERY_LIMITS = "MINISTERS"
): T {
  const limit = QUERY_LIMITS[entityType];
  // Use .limit() to explicitly set max rows (more reliable than .range() alone)
  return query.limit(limit) as T;
}

/**
 * Helper for ministers queries (10k limit)
 * Use this for any query that returns ministers data
 * 
 * @example
 * ```typescript
 * const query = supabase.from('ministers').select('*').eq('is_active', true);
 * const { data } = await withMinistersLimit(query);
 * ```
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function withMinistersLimit<T = any>(query: any) {
  return applyQueryLimit<T>(query, "MINISTERS");
}

/**
 * Helper for credential requests queries (10k limit)
 * Use for credential_requests, credential_request_ministers, and report views
 * 
 * @example
 * ```typescript
 * const query = supabase
 *   .from('vw_credential_request_ministers_extended')
 *   .select('*')
 *   .eq('request_year', 2026);
 * const { data } = await withCredentialRequestsLimit(query);
 * ```
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function withCredentialRequestsLimit<T = any>(query: any) {
  return applyQueryLimit<T>(query, "CREDENTIAL_REQUESTS");
}

/**
 * Helper for churches queries (10k limit)
 * Use this when churches feature is implemented
 * 
 * @example
 * ```typescript
 * const query = supabase.from('churches').select('*').eq('district_id', districtId);
 * const { data } = await withChurchesLimit(query);
 * ```
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function withChurchesLimit<T = any>(query: any) {
  return applyQueryLimit<T>(query, "CHURCHES");
}

/**
 * Helper for reports/analytics queries (10k limit)
 * Use for large aggregated result sets (e.g. pre-aggregated views or RPC results).
 *
 * @example
 * ```typescript
 * const query = supabase.from('some_analytics_view').select('*').eq('year', 2026);
 * const { data } = await withReportsLimit(query);
 * ```
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function withReportsLimit<T = any>(query: any) {
  return applyQueryLimit<T>(query, "REPORTS");
}

/**
 * USAGE GUIDELINES:
 *
 * 1. **Use `.limit()` helpers for direct table queries**:
 *    - `ministers` → `withMinistersLimit`
 *    - `credential_requests` → `withCredentialRequestsLimit`
 *    - `churches` → `withChurchesLimit` (when implemented)
 *
 * 2. **Complex multi-JOIN views — use a single scoped query with a hard cap**:
 *    - Scope with an indexed column (e.g. `request_year`)
 *    - Add `.limit(5000)` as a safety cap
 *    - ⚠️ Never use OFFSET batching on a complex view — it re-scans the full
 *      JOIN tree per batch and will OOM free-tier Postgres instances.
 *    - See `getMinistersInRequests()` in reports.service.ts for the pattern.
 *
 * 3. **Skip limit helpers for small/bounded datasets**:
 *    - Users (< 100), Degrees (< 20), Districts/Regions (< 50)
 *
 * 4. **Performance tips**:
 *    - Select only needed columns — avoids transferring joined data you don't use
 *    - Apply `.eq`/`.in`/`.gte` filters before `.limit()` for smaller payloads
 *    - For aggregations, prefer server-side RPC/views over JS-side aggregation
 *
 * 5. **Signs you hit the 1000-row default cap**:
 *    - Response has exactly 1000 rows even though more exist
 *    - Explicitly set `.limit(N)` with N > 1000
 */
